/**
 * Light and dark, and remembering which.
 *
 * The whole palette lives in `:root` and `:root[data-theme="light"]` in
 * `styles.css`; this module only decides which of the two is in force and
 * stamps `data-theme` on `<html>`. Nothing else in the app reads the theme.
 *
 * Applied **before the app renders** (see the call in `main.ts`), because
 * setting it afterwards paints the wrong theme for a frame — a white flash on
 * a dark ground is worse than either theme.
 *
 * First load follows the operating system. Dark is the fallback when the
 * system expresses no preference, because `geo.clouddgd.net` is dark and the
 * viewer should not be a surprise. An explicit choice overrides the system
 * from then on and survives reloads.
 */

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'gv.theme'

/** The stored choice, or null when the user has never picked one. */
function storedTheme(): Theme | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === 'light' || raw === 'dark' ? raw : null
  } catch {
    return null // private mode, or storage disabled — fall through to the system
  }
}

function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

/** Which theme is in force right now. */
export function currentTheme(): Theme {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'
}

/** Put a theme in force. Does not persist — `setTheme` does that. */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme
}

/** Put a theme in force and remember it. */
export function setTheme(theme: Theme): void {
  applyTheme(theme)
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* the theme still applies for this session */
  }
}

/**
 * Decide the opening theme: an explicit choice if there is one, the system
 * otherwise. Call once, as early as possible.
 */
export function initTheme(): void {
  applyTheme(storedTheme() ?? systemTheme())

  // Follow the system while the user has expressed no preference of their own.
  window.matchMedia?.('(prefers-color-scheme: light)').addEventListener?.('change', () => {
    if (!storedTheme()) applyTheme(systemTheme())
  })
}

/**
 * The control. Labelled with the theme it switches *to*, not the one in force —
 * a button that says "Dark" while the screen is dark reads as a state, and the
 * user has to guess whether pressing it changes anything.
 */
export function createThemeToggle(): HTMLButtonElement {
  const button = document.createElement('button')
  button.className = 'btn btn--small theme-toggle'
  button.id = 'theme-toggle'

  const paint = () => {
    const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark'
    button.textContent = next === 'light' ? '☀ Light' : '☾ Dark'
    button.title = `Switch to the ${next} theme`
    button.setAttribute('aria-label', `Switch to the ${next} theme`)
  }

  button.addEventListener('click', () => {
    setTheme(currentTheme() === 'dark' ? 'light' : 'dark')
    paint()
  })

  paint()
  return button
}
